import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { authToken } from '../globals/Global';
import { createMantra, getMantraData } from '../globals/Services';

export default function MantraList() {
  const [categories, setCategories] = useState([]);
  const [newMantra, setNewMantra] = useState({ name: '', description: '' });
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openEditCategoryDialog, setOpenEditCategoryDialog] = useState(false);
  const [openDeleteCategoryDialog, setOpenDeleteCategoryDialog] = useState(false);

  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);

  const [refetch, setRefetch] = useState(0);
  const triggerRefetch = () => setRefetch(prev => prev + 1);

  const fetchMantraData = useCallback(async () => {
    try {
      const token = authToken || localStorage.getItem('sessionToken');
      const res = await fetch(getMantraData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to fetch data');
      const result = await res.json();
      const mantraData = result?.data || [];

      const transformedCategories = mantraData.map(cat => ({
        id: cat.id,
        name: cat.mantraName,
        description: cat.mantraContent || '',
      }));
      setCategories(transformedCategories);

      if (!selectedCategoryId && transformedCategories.length > 0) {
        setSelectedCategoryId(transformedCategories[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch mantra data:', error);
      toast.error('Error fetching mantra data');
    }
  }, [selectedCategoryId]);

  useEffect(() => {
    fetchMantraData();
  }, [refetch, fetchMantraData]);

  const getAuthToken = () => authToken || localStorage.getItem('sessionToken');

  const handleCreateCategory = async () => {
    try {
      const res = await fetch(createMantra, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          mantraName: newMantra.name,
          mantraContent: newMantra.description,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Could not create mantra.');
      toast.success('Mantra created successfully!');
      setOpenCategoryDialog(false);
      setNewMantra({ name: '', description: '' });
      triggerRefetch();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;
    try {
      const res = await fetch('/api/aartiDetails/aarti/updateAartiByAdmin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          aartiId: Number(editingCategory.id),
          aartiTitleName: editingCategory.titleName,
          aartiGodName: editingCategory.name,
          aartiGodImageUrl: editingCategory.imageUrl,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Could not update mantra.');
      toast.success('Mantra updated successfully!');
      setOpenEditCategoryDialog(false);
      triggerRefetch();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return;
    try {
      const res = await fetch(`/api/aartiDetails/aarti/deleteAartiByAdmin/${deletingCategory.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error('Could not delete mantra.');
      toast.success('Mantra deleted successfully!');
      setOpenDeleteCategoryDialog(false);
      setSelectedCategoryId(null);
      triggerRefetch();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setOpenEditCategoryDialog(true);
  };

  const openDeleteCategory = (cat) => {
    setDeletingCategory(cat);
    setOpenDeleteCategoryDialog(true);
  };

  return (
    <Box sx={{ height: '100vh', overflow: 'hidden', p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="600">Mantra List</Typography>
        <Button variant="contained" color="primary" onClick={() => setOpenCategoryDialog(true)}
          sx={{ 
          minWidth: '120px',
          fontWeight: 'bold',
          textTransform: 'none'
        }}
          >
          + Add Mantra
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Card sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flex: 1, overflow: 'auto' }}>
            {categories.length > 0 ? categories.map(cat => (
              <Box
                key={cat.id}
                onClick={() => setSelectedCategoryId(cat.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 1,
                  border: 1, borderRadius: 2, cursor: 'pointer',
                  borderColor: selectedCategoryId === cat.id ? 'primary.main' : 'grey.300',
                  backgroundColor: selectedCategoryId === cat.id ? 'primary.lightest' : 'white'
                }}
              >
                <Box flexGrow={1}>
                  <Typography variant="body2" color="text.secondary">ID: {cat.id}</Typography>
                  <Typography fontWeight="bold">{cat.name}</Typography>
                  <Typography variant="body2" color="text.secondary">Description: {cat.description}</Typography>
                </Box>
                <Box display="flex" flexDirection="column" gap={1}>
                  <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openEditCategory(cat); }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openDeleteCategory(cat); }}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            )) : (
              <Typography color="text.secondary" mt={4}>No mantra found.</Typography>
            )}
          </CardContent>
        </Card>

        </Box>
      </Box>

      {/* Create Mantra Dialog */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Mantra</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Mantra Name"
            margin="normal"
            value={newMantra.name}
            onChange={e => setNewMantra({ ...newMantra, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            value={newMantra.description}
            onChange={e => setNewMantra({ ...newMantra, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateCategory} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditCategoryDialog} onClose={() => setOpenEditCategoryDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Mantra</DialogTitle>
        {editingCategory && (
          <>
            <DialogContent>
              <TextField fullWidth label="Mantra Name" margin="normal" value={editingCategory.name} onChange={e => setEditingCategory({ ...editingCategory, name: e.target.value })} />
              <TextField fullWidth label="Description" margin="normal" value={editingCategory.description} onChange={e => setEditingCategory({ ...editingCategory, description: e.target.value })} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEditCategoryDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateCategory} variant="contained">Save Changes</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteCategoryDialog} onClose={() => setOpenDeleteCategoryDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete: <strong>{deletingCategory?.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteCategory} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
      
      {/* Add ToastContainer at the end */}
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            
    </Box>
  );
}
